import _ from 'lodash';
import {
	DEFAULT_SUBSCRIPTION,
	PLUS_OVERVIEW_PLAN_ID,
	PERPETUAL_OVERVIEW_PLAN_ID,
	STANDARD_OVERVIEW_PLAN_ID,
	PACKAGES_CONFIG,
	DESKTOP_APPS,
	ADDON_TYPE,
	getMemberAddonsIds,
	CURRENCY_PARAM
} from '../config';
		
import { SUBSCRIPTION_UPGRADE, SUBSCRIPTION_PLUS_OVERVIEW, SUBSCRIPTION_PERPETUAL_OVERVIEW, SUBSCRIPTION } from 'routes';

const getCurrentPlan = (company) => {
	return (
		_.get(company, 'paymentAccount.products.essential.subscription') ||
		_.get(company, 'paymentAccount.products.teamplus.subscription') ||

		_.get(company, 'paymentAccount.products.web.subscription') ||
		_.get(company, 'paymentAccount.products.photo.subscription') ||
		_.get(company, 'paymentAccount.products.designer.subscription')
	) || {};
};

export const getCurrencyUsedInCB = (company) => {
	const { perpetualAddons, subscription } = getSubscriptionsAndAddons(company, true);
	let result;
	if (!_.isEmpty(subscription)) {
		({ currency: result } = convertPlanIdIntoSubscription(subscription.planId));
	} else if (!_.isEmpty(perpetualAddons)) {
		({ currency: result } = convertPlanIdIntoSubscription(perpetualAddons[0].id));
	}
	return result;
};

export const getSubscriptionsAndAddons = (company, includeInactive) => {
	const allProducts = _.get(company, 'paymentAccount.products');

	let memberAddons = null;
	let perpetualAddons = null;
	let subscription = null;

	if (allProducts) {
		const objectKeys = Object.keys(allProducts);

		objectKeys.forEach(key => {
			const addonProduct = _.get(allProducts[key], 'addon');
			if (addonProduct && (includeInactive || addonProduct.active)) {
				if (isPerpetualAddon(key)) {
					perpetualAddons = perpetualAddons || [];
					perpetualAddons.push(addonProduct);
				} else if (isMemberAddon(key)) {
					memberAddons = memberAddons || [];
					memberAddons.push(addonProduct);
				}
			}

			const subscriptionProduct = _.get(allProducts[key], 'subscription');
			if (subscriptionProduct && (includeInactive || subscriptionProduct.active)) {
				subscription = subscriptionProduct;
			}
		});
	}

	return {
		subscription,
		perpetualAddons,
		memberAddons
	};
};

/**
 * Get Current planId for the company
 *
 * @param {object} company object
 * @returns planId format [plan-duration-currency]
 */
export const getCurrentPlanId = (company) => {
	let { active, planId } = getCurrentPlan(company);

	// P9052-10593: return planId only if subscription is active
	if (active) {
		return planId;
	}
};

/**
 * Convert planId into subscription object
 *
 * @param {*} planId format [plan-duration-currency]
 * @returns activeSubscription object
 */
export const convertPlanIdIntoSubscription = (planId) => {
	if (planId) {
		const [ plan, period, currency ] = planId.split('-');
		return { plan, period, currency };
	} else {
		return DEFAULT_SUBSCRIPTION;
	}
};

/**
 * Get plan name from planId
 *
 * @param {object} company object
 * @returns plan name
 */
export const getCurrentPlanName = (company) => {
	const planId = getCurrentPlanId(company);
	const { plan } = convertPlanIdIntoSubscription(planId);
	return plan;
};

export const getSubscriptionRedirectUrl = (planId, companyId, currency, queryVars) => {

	let component;
	
	if (planId === PLUS_OVERVIEW_PLAN_ID) {
		component = SUBSCRIPTION_PLUS_OVERVIEW;
	} else if (planId === PERPETUAL_OVERVIEW_PLAN_ID) {
		component = SUBSCRIPTION_PERPETUAL_OVERVIEW;
	} else if (planId === STANDARD_OVERVIEW_PLAN_ID) {
		component = SUBSCRIPTION;
	} else {
		component = SUBSCRIPTION_UPGRADE;
	}

	const selectedCurrency = (planId === PLUS_OVERVIEW_PLAN_ID ||
		planId === PERPETUAL_OVERVIEW_PLAN_ID ||
		planId === STANDARD_OVERVIEW_PLAN_ID) && !currency
		?
		DEFAULT_SUBSCRIPTION.currency
		:
		currency;

	const componentPath = component.replace(':companyId', '#' + companyId)
		.replace(':planId', planId)
		.replace(':currency?', selectedCurrency);

	if (planId === STANDARD_OVERVIEW_PLAN_ID && currency) {
		queryVars.push(`${CURRENCY_PARAM}=${currency}`);
		queryVars.currency = currency;
	}

	const queryPath = queryVars ? `/?${queryVars.join('&')}` : '';
	return `${window.location.origin}${componentPath}${queryPath}`;
};

export const isDesktopOnlyPlan = (plan) => {
	if (!plan) return false;
	const planId = plan.split('-')[0];

	const isDesktopOnlyPlan = PACKAGES_CONFIG[planId].isPerpetual ||
		PACKAGES_CONFIG[planId].isCreativeToolsPlan && !PACKAGES_CONFIG[planId].isCloudSuitePlan;
	return isDesktopOnlyPlan;
};

export const isPerpetualPlan = (plan) => {
	if (!plan) return false;
	const planId = plan.split('-')[0];
	return PACKAGES_CONFIG[planId].isPerpetual;
};


export const getTotalProductsForCompany = (company) => {
	const { subscription, perpetualAddons } = getSubscriptionsAndAddons(company);

	let nrOfProductUserOwns = 0;
	if (!_.isEmpty(subscription)) {
		nrOfProductUserOwns++;
	}
	if (!_.isEmpty(perpetualAddons)) {
		nrOfProductUserOwns += perpetualAddons.length;
	}

	return nrOfProductUserOwns;
};


export const isValidNonZeroQuantity = (value) => {
	return /^\d+$/.test(value) && _.isInteger(parseInt(value)) && parseInt(value) > 0;
};

export const isPerpetualAddon = (addonId) => {
	return !_.isEmpty(DESKTOP_APPS[addonId.split('_')[0].toUpperCase()]);
};

export const isMemberAddon = (addonId) => {
	return getMemberAddonsIds().includes(addonId.split('-')[0]);
};

export const getAddonType = (addonId) => {
	if (isPerpetualAddon(addonId)) {
		return ADDON_TYPE.PERPETUAL;
	} else if (isMemberAddon(addonId)) {
		return ADDON_TYPE.MEMBER;
	}
};

export const getMembersPaidQuantity = (company) => {
	const { memberAddons } = getSubscriptionsAndAddons(company);
	let result = 0;

	if (!_.isEmpty(memberAddons)) {
		memberAddons.forEach((addon) => {
			result += addon.paidQuantity;
		});
	}

	return result;
};

export const getMembersUsedQuantity = (company) => {
	const { memberAddons } = getSubscriptionsAndAddons(company);
	let result = 0;

	if (!_.isEmpty(memberAddons)) {
		memberAddons.forEach((addon) => {
			result += addon.usedQuantity;
		});
	}

	return result;	
};

export const getKeysOfMembersAddonObject = (company) => {
	const { memberAddons } = getSubscriptionsAndAddons(company);

	let result = [];

	if (!_.isEmpty(memberAddons)) {
		memberAddons.forEach((addon) => {
			result.push(addon.id.split('-')[0]);
		});
	}

	return result;	
};

export const getNonObsoleteMemberAddonForPlan = (planId) => {
	// isObsolete: false is the addon what MW/user can purchase
	// isObsolete: true is the addon what user/MW cannot purchase but user can have if user got it earlier.
	let result = null;

	const selectedAddons = PACKAGES_CONFIG[planId].selectedAddons;

	if (!_.isEmpty(selectedAddons)) {
		Object.keys(selectedAddons).forEach((addonKey) => {
			if (!selectedAddons[addonKey].isObsolete) {
				result = addonKey;
			}
		});
	}

	return result;
};

export const isPerpetualOwner = (company) => {
	const { perpetualAddons } = getSubscriptionsAndAddons(company);
	return !_.isEmpty(perpetualAddons);
};


// WEBPACK FOOTER //
// ./src/utils/subscription.js